import { Member, MemberMap } from '@nextforce/api'
import { CheckBox, Input, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import { MemberCard } from 'chat/members/views/card'
import { MemberSlugList } from 'chat/members/views/slug_list'
import { useCallback, useMemo } from 'react'
import { View } from 'react-native'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import { SafeAreaView } from 'react-native-safe-area-context'
import { RoundedButton } from 'shared/components/rounded-button'
import { toggleItemInObjectMap } from 'shared/utils'

interface AwardTransferGoldStarViewProps {
  type: 'award' | 'transfer'
  members: MemberMap
  onMembersChanged(members: MemberMap): void
  loading: boolean
  onAwardTransferGoldStars: () => void
  sendMessageToCompanyGroup: boolean
  sendMessageToStoreGroups: boolean
  optionalMessage?: string
  starCount?: string
  onMemberRemoved?: (member: Member) => void
  setSendMessageToCompanyGroup: React.Dispatch<React.SetStateAction<boolean>>
  setSendMessageToStoreGroups: React.Dispatch<React.SetStateAction<boolean>>
  setOptionalMessage: React.Dispatch<React.SetStateAction<string | undefined>>
  setStarCount: React.Dispatch<React.SetStateAction<string | undefined>>
}

export function AwardTransferGoldStarView({
  type,
  members,
  onMembersChanged,
  loading,
  onAwardTransferGoldStars,
  sendMessageToCompanyGroup,
  sendMessageToStoreGroups,
  setSendMessageToCompanyGroup,
  setSendMessageToStoreGroups,
  optionalMessage,
  setOptionalMessage,
  starCount,
  setStarCount,
}: AwardTransferGoldStarViewProps) {
  const { theme } = useTheme()
  const styles = useStyles(theme)

  const isButtonDisabled = useMemo(() => {
    if (type === 'transfer') {
      return Object.values(members).length === 0
    }
    return (
      (!sendMessageToCompanyGroup && !sendMessageToStoreGroups) ||
      Object.values(members).length === 0
    )
  }, [type, sendMessageToCompanyGroup, sendMessageToStoreGroups, members])

  const onMemberRemoved = useCallback(
    (member: Member) => {
      const updatedMembers = toggleItemInObjectMap(members, member.id, member)
      onMembersChanged(updatedMembers)
    },
    [members, onMembersChanged]
  )

  const DetailsView =
    type === 'award' ? AwardGoldStarDetails : TransferGoldStarDetails

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <KeyboardAwareScrollView style={styles.container} enableOnAndroid={true}>
        <DetailsView
          type={type}
          members={members}
          onMembersChanged={onMembersChanged}
          loading={loading}
          onAwardTransferGoldStars={onAwardTransferGoldStars}
          sendMessageToCompanyGroup={sendMessageToCompanyGroup}
          sendMessageToStoreGroups={sendMessageToStoreGroups}
          setSendMessageToCompanyGroup={setSendMessageToCompanyGroup}
          setSendMessageToStoreGroups={setSendMessageToStoreGroups}
          optionalMessage={optionalMessage}
          setOptionalMessage={setOptionalMessage}
          onMemberRemoved={onMemberRemoved}
          starCount={starCount}
          setStarCount={setStarCount}
        />

        <RoundedButton
          onPress={() => onAwardTransferGoldStars()}
          disabled={isButtonDisabled}
          danger
          title={type === 'award' ? t('award') : t('transfer')}
          loading={loading}
          buttonStyle={styles.buttonStyle}
        />
      </KeyboardAwareScrollView>
    </SafeAreaView>
  )
}

function AwardGoldStarDetails({
  members,
  sendMessageToCompanyGroup,
  sendMessageToStoreGroups,
  setSendMessageToCompanyGroup,
  setSendMessageToStoreGroups,
  optionalMessage,
  setOptionalMessage,
  onMemberRemoved,
}: AwardTransferGoldStarViewProps) {
  const { theme } = useTheme()
  const styles = useStyles(theme)
  return (
    <View>
      <View style={styles.membersLabelContainer}>
        <Text style={styles.label}>
          {t('recipient', { count: Object.values(members).length })}
        </Text>
      </View>
      <MemberSlugList
        members={Object.values(members)}
        onMemberRemoved={onMemberRemoved}
      />
      <Text style={styles.checkBoxHeadingLabel}>{t('postToGroups')}</Text>
      <CheckBox
        checked={sendMessageToCompanyGroup}
        title={t('sendMessageToCompanyGroup')}
        onPress={() => {
          setSendMessageToCompanyGroup(!sendMessageToCompanyGroup)
        }}
        containerStyle={styles.checkboxContainer}
        wrapperStyle={styles.checkboxWrapper}
        textStyle={styles.checkboxText}
      />

      <CheckBox
        checked={sendMessageToStoreGroups}
        title={t('sendMessageToRecepientsStoreGroups')}
        onPress={() => {
          setSendMessageToStoreGroups(!sendMessageToStoreGroups)
        }}
        containerStyle={styles.checkboxContainer}
        wrapperStyle={styles.checkboxWrapper}
        textStyle={styles.checkboxText}
      />

      <View style={styles.inputContainer}>
        <Input
          style={styles.input}
          placeholder={t('optionalMessage')}
          value={optionalMessage}
          onChangeText={inputText => setOptionalMessage(inputText)}
          multiline
          enablesReturnKeyAutomatically
        />
      </View>
    </View>
  )
}

function TransferGoldStarDetails({
  members,
  starCount,
  setStarCount,
}: AwardTransferGoldStarViewProps) {
  const member = Object.values(members)[0]
  const styles = useStyles()

  if (!member) return null
  return (
    <View>
      <Text style={styles.label}>
        {t('recipient', { count: Object.values(members).length })}
      </Text>
      <View style={styles.memberCardContainer}>
        <MemberCard member={member} bottomDivider={false} />
      </View>
      <Text style={styles.label}>
        {t('enterTheNumberOfGoldStarsToTransfer')}
      </Text>
      <Input
        style={styles.textInput}
        placeholderTextColor={styles.textInputPlaceholder.color}
        placeholder={t('enterTheNumberOfGoldStarsToTransfer')}
        value={starCount}
        onChangeText={setStarCount}
        maxLength={20}
        keyboardType="number-pad"
      />
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
    padding: 10,
    marginTop: 10,
  },
  membersLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: 5,
  },
  label: {
    paddingHorizontal: 10,
    fontSize: 17,
    color: theme.colors?.grey1,
    fontFamily: font.bold,
  },
  checkBoxHeadingLabel: {
    paddingHorizontal: 10,
    marginVertical: 5,
    marginBottom: 10,
    fontSize: 17,
    color: theme.colors?.grey1,
    fontFamily: font.bold,
  },
  checkboxContainer: {
    maxWidth: 300,
    padding: 0,
    margin: 0,
    minHeight: 30,
    backgroundColor: 'transparent',
  },
  checkboxWrapper: {
    padding: 0,
    margin: 0,
    alignItems: 'flex-start',
  },
  checkboxText: {
    textAlign: 'justify',
    fontSize: 17,
    marginLeft: 2,
  },
  inputContainer: {
    backgroundColor: theme.colors?.grey5,
    margin: 10,
  },
  input: {
    height: 100,
    textAlignVertical: 'top',
    marginVertical: 5,
    borderColor: theme.colors?.grey4,
    color: theme.colors?.black,
    fontStyle: 'italic',
  },
  buttonStyle: {
    margin: 5,
    marginTop: 15,
  },
  textInput: {
    fontSize: 17,
    fontStyle: 'italic',
    color: theme.colors?.black,
    paddingHorizontal: 10,
    width: '100%',
    backgroundColor: theme.colors.grey5,
    height: 50,
    marginVertical: 10,
  },
  textInputPlaceholder: {
    color: 'lightgray',
  },
  memberCardContainer: {
    marginBottom: 10,
  },
}))
